import { LoadingButton } from "@mui/lab";
import { MenuItem, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { useRecordContext } from "react-admin";
import { setUserRoles } from "../../api/backoffice.api";
import { currentUser, grants } from "../../backoffice.access_control";
import { useCurrentDrivingSchoolId } from "../../hooks/useCurrentDrivingSchoolId";
import { FormCard } from "../../misc/FormCard";

interface UserWithAssignableRole {
  id: string;
  roles: Array<string>;
}

const managerRoleChoices = [
  { id: "drivingSchoolManager", name: "Fahrschulleiter" },
  { id: "instructor", name: "Fahrlehrer" },
  { id: "drivingSchoolCustomerService", name: "Kundenservice" },
  { id: "drivingSchoolAccountant", name: "Buchhaltung" },
];
const managerRoleIds = new Set(managerRoleChoices.map((choice) => choice.id));

export function DrivingSchoolUserRolesForm() {
  const user = useRecordContext<UserWithAssignableRole>();
  const drivingSchoolId = useCurrentDrivingSchoolId();
  const currentRole = user ? managerRoleChoices.find((it) => user.roles.includes(it.id))?.id : undefined;
  const initialRole = currentRole ?? "instructor";
  const [savedRole, setSavedRole] = useState<string>(initialRole);
  const [selectedRole, setSelectedRole] = useState<string>(initialRole);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSavedRole(initialRole);
    setSelectedRole(initialRole);
  }, [initialRole, user?.id]);

  if (!user) {
    return null;
  }

  const { id, roles } = user;
  const isEditingOwnRoleAsDrivingSchoolManager =
    currentUser?.uid === id && roles.includes("drivingSchoolManager") && !grants.has("admin");
  const hasChanges = selectedRole !== savedRole;

  async function saveRole() {
    const rolesToKeep = roles.filter((role) => !managerRoleIds.has(role));
    const updatedRoles = [...rolesToKeep, selectedRole];
    setLoading(true);
    try {
      await runSafely(() => setUserRoles(id, updatedRoles, drivingSchoolId ? [drivingSchoolId] : []), {
        successMessage: "Rolle erfolgreich gespeichert.",
        errorMessage: "Fehler beim Speichern der Rolle.",
        onSuccess: () => setSavedRole(selectedRole),
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <FormCard title="Rolle" showSaveButton={false} sx={{ padding: "25px 30px" }}>
      <TextField
        select
        size="small"
        label=""
        fullWidth
        value={selectedRole}
        onChange={(event) => {
          const value = event.target.value;
          if (isEditingOwnRoleAsDrivingSchoolManager && value !== "drivingSchoolManager") {
            return;
          }
          setSelectedRole(value);
        }}
        sx={{ mb: 4 }}
      >
        {managerRoleChoices.map((role) => (
          <MenuItem
            key={role.id}
            value={role.id}
            disabled={isEditingOwnRoleAsDrivingSchoolManager && role.id !== "drivingSchoolManager"}
            sx={{
              "&.Mui-disabled": {
                color: "text.disabled",
                WebkitTextFillColor: (theme) => theme.palette.text.disabled,
                opacity: 1,
              },
            }}
          >
            {role.name}
          </MenuItem>
        ))}
      </TextField>
      {hasChanges && (
        <LoadingButton
          variant="contained"
          loading={loading}
          onClick={() => void saveRole()}
          disabled={!selectedRole}
          sx={{ mb: 2 }}
        >
          Speichern
        </LoadingButton>
      )}
    </FormCard>
  );
}
